import { IBox, IOrientedBox } from './exportLabelTypes'

// A rectangle by its centre, half sides and turn, worked out once so a test allocates nothing.
interface IFrame {
  x: number
  y: number
  halfWidth: number
  halfHeight: number
  cos: number
  sin: number
}

interface IEntry {
  box: IBox
  ownerId: string
  frame?: IFrame
}

// Shadows that only touch, up to float noise in the turned corners, do not overlap.
const TOUCH_EPSILON_PX = 1e-6
const RIGHT_ANGLE_EPSILON = 1e-9

export const boxesIntersect = (a: IBox, b: IBox): boolean =>
  a.x < b.x + b.width &&
  b.x < a.x + a.width &&
  a.y < b.y + b.height &&
  b.y < a.y + a.height

const frameOf = ({
  centre,
  width,
  height,
  angleRad,
}: IOrientedBox): IFrame => ({
  x: centre.x,
  y: centre.y,
  halfWidth: width / 2,
  halfHeight: height / 2,
  cos: Math.cos(angleRad),
  sin: Math.sin(angleRad),
})

const uprightFrame = (box: IBox): IFrame => ({
  x: box.x + box.width / 2,
  y: box.y + box.height / 2,
  halfWidth: box.width / 2,
  halfHeight: box.height / 2,
  cos: 1,
  sin: 0,
})

// A shape turned by a right angle fills its upright box, which is then the exact test.
const collisionFrame = (shape?: IOrientedBox): IFrame | undefined =>
  shape && Math.abs(Math.sin(2 * shape.angleRad)) > RIGHT_ANGLE_EPSILON
    ? frameOf(shape)
    : undefined

const radiusOn = (f: IFrame, nx: number, ny: number): number =>
  f.halfWidth * Math.abs(f.cos * nx + f.sin * ny) +
  f.halfHeight * Math.abs(f.cos * ny - f.sin * nx)

const overlapOn = (a: IFrame, b: IFrame, nx: number, ny: number): boolean =>
  Math.abs((a.x - b.x) * nx + (a.y - b.y) * ny) <
  radiusOn(a, nx, ny) + radiusOn(b, nx, ny) - TOUCH_EPSILON_PX

// Separating axes: two rectangles are apart when their shadows on one of their four edge
// directions do not overlap.
const framesIntersect = (a: IFrame, b: IFrame): boolean =>
  overlapOn(a, b, a.cos, a.sin) &&
  overlapOn(a, b, -a.sin, a.cos) &&
  overlapOn(a, b, b.cos, b.sin) &&
  overlapOn(a, b, -b.sin, b.cos)

// An entry's box bounds its shape, so boxes apart means shapes apart.
const entriesIntersect = (
  box: IBox,
  frame: IFrame | undefined,
  entry: IEntry,
): boolean =>
  boxesIntersect(box, entry.box) &&
  (!(frame || entry.frame) ||
    framesIntersect(
      frame ?? uprightFrame(box),
      entry.frame ?? uprightFrame(entry.box),
    ))

// Cells cover the page only: a box reaching past an edge is filed under the cells it overlaps,
// and one wholly off the page is filed nowhere, since nothing placed on the page can meet it.
// An entry with a shape is filed by its box and collides by its shape.
export class LabelGridIndex {
  private readonly columns: number
  private readonly rows: number
  private readonly cells: IEntry[][]

  public constructor(
    private readonly width: number,
    private readonly height: number,
    private readonly cellPx: number = 64,
  ) {
    this.columns = Math.max(1, Math.ceil(width / cellPx))
    this.rows = Math.max(1, Math.ceil(height / cellPx))
    this.cells = Array.from({ length: this.columns * this.rows }, () => [])
  }

  public insert(box: IBox, ownerId: string, shape?: IOrientedBox): void {
    const entry = { box, ownerId, frame: collisionFrame(shape) }
    this.forEachCell(box, cell => cell.push(entry))
  }

  public hitsBox(
    box: IBox,
    ignoreOwnerId?: string,
    shape?: IOrientedBox,
  ): boolean {
    const frame = collisionFrame(shape)
    let hit = false
    this.forEachCell(box, cell => {
      hit ||= cell.some(
        entry =>
          entry.ownerId !== ignoreOwnerId &&
          entriesIntersect(box, frame, entry),
      )
    })
    return hit
  }

  private forEachCell(box: IBox, visit: (cell: IEntry[]) => void): void {
    if (
      box.x >= this.width ||
      box.y >= this.height ||
      box.x + box.width <= 0 ||
      box.y + box.height <= 0
    ) {
      return
    }
    const clamp = (value: number, count: number): number =>
      Math.min(count - 1, Math.max(0, Math.floor(value / this.cellPx)))
    const left = clamp(box.x, this.columns)
    const right = clamp(box.x + box.width, this.columns)
    const top = clamp(box.y, this.rows)
    const bottom = clamp(box.y + box.height, this.rows)
    for (let row = top; row <= bottom; row++) {
      for (let column = left; column <= right; column++) {
        visit(this.cells[row * this.columns + column])
      }
    }
  }
}
